import { auth } from "../config/firebaseConfig";
import { buildJsonRequest } from "./apiClient";

const resolveAuthUser = (user) => {
  const current = auth.currentUser;
  if (user && typeof user.getIdToken === "function") return user;
  if (current && typeof current.getIdToken === "function") return current;
  return null;
};

const fetchSameOriginJson = async (path, fetchOptions = {}, { allow404 = false } = {}) => {
  const response = await fetch(path, fetchOptions);

  if (allow404 && response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const err = new Error(`HTTP ${response.status} from ${path}: ${text}`);
    err.status = response.status;
    throw err;
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/json")) {
    const text = await response.text().catch(() => "");
    throw new Error(`Expected JSON from ${path} but received ${contentType || "unknown"} ${text ? `- ${text}` : ""}`);
  }

  return await response.json();
};

const buildFallbackProfile = (user) => {
  if (!user?.uid) return null;

  return {
    retailerId: user.uid,
    uid: user.uid,
    email: user.email || "",
    publicEmail: user.email || "",
    ownerUid: user.uid,
    publicContact: {
      email: user.email || "",
    },
  };
};

export const fetchSelfRetailerProfile = async (
  user = auth.currentUser,
  options = {}
) => {
  const authUser = resolveAuthUser(user);
  const baseUser = authUser || user;

  if (!baseUser?.uid) {
    return buildFallbackProfile(baseUser);
  }

  try {
    const token = await authUser?.getIdToken?.(options?.forceTokenRefresh !== false);
    if (!token) {
      return buildFallbackProfile(baseUser);
    }

    const retailerIdHint = String(options?.retailerId || "").trim();
    const query = retailerIdHint ? `?retailerId=${encodeURIComponent(retailerIdHint)}` : "";
    const fetchOptions = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    // Prefer same-origin `/api` (CRA proxy / Hosting rewrite). If it's missing, fall back gracefully.
    let payload = await fetchSameOriginJson(`/api/profile/self${query}`, fetchOptions, { allow404: true });
    if (!payload) {
      return buildFallbackProfile(baseUser);
    }

    if (payload?.ok && payload?.profile && typeof payload.profile === "object") {
      return {
        ...buildFallbackProfile(baseUser),
        ...payload.profile,
        retailerId: payload?.retailerId || payload?.profile?.retailerId || baseUser.uid,
        retailerIds: Array.isArray(payload?.retailerIds)
          ? payload.retailerIds
          : Array.isArray(payload?.profile?.retailerIds)
            ? payload.profile.retailerIds
            : [],
        accountRetailers: Array.isArray(payload?.accountRetailers)
          ? payload.accountRetailers
          : Array.isArray(payload?.profile?.accountRetailers)
            ? payload.profile.accountRetailers
            : [],
      };
    }
  } catch (error) {
    if (options?.throwOnUnauthorized && (error?.status === 401 || error?.status === 403)) {
      throw error;
    }
    console.warn("[profileApi] self profile fetch failed", error);
  }

  return buildFallbackProfile(baseUser);
};

export const bootstrapRetailerProfile = async (
  {
    businessName,
    ownerName,
    email,
  },
  user = auth.currentUser
) => {
  const authUser = resolveAuthUser(user);
  if (!authUser?.uid) {
    throw new Error("missing_auth_user");
  }

  const token = await authUser.getIdToken();
  if (!token) {
    throw new Error("missing_id_token");
  }

  const fetchOptions = buildJsonRequest(
    "POST",
    {
      businessName: String(businessName || "").trim(),
      ownerName: String(ownerName || "").trim(),
      email: String(email || authUser.email || "").trim().toLowerCase(),
    },
    {
      Authorization: `Bearer ${token}`,
    }
  );

  try {
    // Prefer same-origin `/api` to avoid cross-origin CORS failures.
    const payload = await fetchSameOriginJson("/api/profile/bootstrap", fetchOptions, { allow404: true });

    if (!payload) {
      return { ok: false, error: "not_found", status: 404 };
    }

    if (!payload?.ok) {
      return { ok: false, error: payload?.error || "profile_bootstrap_failed" };
    }

    return payload;
  } catch (error) {
    console.warn("[profileApi] bootstrap failed", error);
    return { ok: false, error: "bootstrap_failed", message: error?.message || String(error) };
  }
};

export default fetchSelfRetailerProfile;
